import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, CalendarX, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { VisiteDetailContent } from '../components/visites/VisiteDetailDialog';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateFr(iso) {
    if (!iso) return '-';
    try { return format(new Date(iso), 'dd MMM yyyy', { locale: fr }); } catch { return iso; }
}

export default function VisitesAReprogrammerPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVisite, setSelectedVisite] = useState(null);
    const [config, setConfig] = useState(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/visites/reprogrammer/list`, { withCredentials: true });
            setItems(data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList();
        axios.get(`${API}/api/visites/config`, { withCredentials: true })
            .then(({ data }) => setConfig(data))
            .catch(() => setConfig(null));
    }, [fetchList]);

    const handleOpenVisite = async (visiteId) => {
        try {
            const { data } = await axios.get(`${API}/api/visites/${visiteId}`, { withCredentials: true });
            setSelectedVisite(data);
        } catch {
            // ignore
        }
    };

    const handleVisiteRefresh = async () => {
        await fetchList();
        // Fermer si la visite n'est plus en decalee
        if (selectedVisite) {
            try {
                const { data } = await axios.get(`${API}/api/visites/${selectedVisite.id}`, { withCredentials: true });
                if (data.statut !== 'decalee') {
                    setSelectedVisite(null);
                } else {
                    setSelectedVisite(data);
                }
            } catch {
                setSelectedVisite(null);
            }
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="visites-reprogrammer-page">
            <div className="mb-6 flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/')}
                    data-testid="back-to-dashboard-btn"
                    className="text-slate-600 hover:text-slate-900"
                >
                    <ArrowLeft size={16} className="mr-1" /> Tableau de bord
                </Button>
            </div>

            <div className="mb-6 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-orange-50">
                    <CalendarX size={24} className="text-orange-500" strokeWidth={2} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Visites à reprogrammer
                    </h1>
                    <p className="mt-1 text-slate-500 text-sm">
                        Liste des visites avec le statut « À reprogrammer ». Elles disparaissent automatiquement une fois le statut changé.
                    </p>
                </div>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-10 flex justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-slate-500 text-sm" data-testid="empty-state">
                            Aucune visite à reprogrammer pour le moment.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold text-slate-600 uppercase tracking-wider text-xs">Nom</th>
                                        <th className="text-left px-5 py-3 font-semibold text-slate-600 uppercase tracking-wider text-xs">Visite annulée le</th>
                                        <th className="text-left px-5 py-3 font-semibold text-slate-600 uppercase tracking-wider text-xs">Remarque</th>
                                        <th className="text-right px-5 py-3 font-semibold text-slate-600 uppercase tracking-wider text-xs">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {items.map((it) => (
                                        <tr key={it.id} className="hover:bg-slate-50 transition-colors" data-testid={`row-${it.id}`}>
                                            <td className="px-5 py-3 font-medium text-slate-900">{it.prospect_nom || '-'}</td>
                                            <td className="px-5 py-3 text-slate-600">{formatDateFr(it.date_annulation)}</td>
                                            <td className="px-5 py-3 text-slate-600 max-w-md">
                                                <span className="line-clamp-2" title={it.remarque}>{it.remarque || <span className="italic text-slate-400">Aucune remarque</span>}</span>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="text-slate-700 border-slate-300 hover:bg-slate-100"
                                                    onClick={() => handleOpenVisite(it.id)}
                                                    data-testid={`view-visite-${it.id}`}
                                                >
                                                    <Eye size={14} className="mr-1" /> Aperçu
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!selectedVisite} onOpenChange={(open) => !open && setSelectedVisite(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
                    {selectedVisite && (
                        <VisiteDetailContent
                            visite={selectedVisite}
                            config={config}
                            onClose={() => setSelectedVisite(null)}
                            onEdit={() => { /* edit depuis liste non prioritaire */ }}
                            onRefresh={handleVisiteRefresh}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
